import styles from '../StatisticPage.module.scss';

import { LinearProgress } from '@mui/material';
import { DateTime } from 'luxon';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

import { StatisticSupplierSheetTable } from '@work4all/components/lib/dataDisplay/basic-table/reporting/StatisticCustomerSheet/StatisticSupplierSheetTable';

import { useDataProvider } from '@work4all/data';

import { StatisticSupplierDatasheet } from '@work4all/models/lib/Classes/StatisticSupplierDatasheet.entity';
import { Supplier } from '@work4all/models/lib/Classes/Supplier.entity';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { SupplierPickerField } from '../../../components/entity-picker/SupplierPickerField';
import { settings, useSetting } from '../../../settings';
import { useSupplier } from '../../files/detail/components/queries/use-supplier';
import { GeneralSetting, useGeneralSetting } from '../util/use-general-setting';
import { useOldestInvoice } from '../util/use-oldest-invoice';
import { useUpdateSearchParams } from '../util/use-update-search-params';

import { useStatisticNavigation } from './hooks/use-statistics-navigation';
import { StatisticDateWheelPickerField } from './statistic-date-wheel-picker-field/StatisticDateWheelPickerField';

export enum StatisticSupplierSheetContainerSearchParamKeys {
  year = 'year',
  id = 'id',
}

export function StatisticSupplierSheetContainer() {
  const oldestInvoice = useOldestInvoice();
  const startBusinessYearSetting = useGeneralSetting({
    name: GeneralSetting.startBusinessYear,
  });
  const startBusinessYearSettingValue =
    startBusinessYearSetting?.data?.[0]?.value || 1;

  const [searchParams] = useSearchParams();
  const updateSearchParams = useUpdateSearchParams();

  const supplierId = searchParams.get(
    StatisticSupplierSheetContainerSearchParamKeys.id
  );
  const supplierResponse = useSupplier(supplierId);
  const supplier = supplierResponse?.supplier;

  const { t } = useTranslation();

  const { currentYear, maxYear, setCurrentYear } = useStatisticNavigation();

  const tablesLayoutBorders = useSetting(
    settings.tablesLayoutBordersStatistics()
  );

  const requestData: DataRequest = useMemo(() => {
    const startDate = DateTime.fromJSDate(currentYear);
    const data: StatisticSupplierDatasheet = {
      sdObjMemberId: null,
      columns: [
        {
          month: null,
          statistics: [
            {
              value: null,
              statisticName: null,
              number: null,
              name: null,
            },
          ],
        },
      ],
    };
    return {
      entity: Entities.statisticSupplierDatasheet,
      data: data,
      vars: {
        rangeStart: startDate.toFormat('yyyy-MM-dd'),
        sdobjMemberCode: supplier?.id,
      },
      completeDataResponse: true,
    };
  }, [currentYear, supplier]);

  const data = useDataProvider(requestData, !supplier);

  const fiscalYears: number[] = Array.from(
    new Set(
      ((data.data?.[0]?.columns as { month: string }[]) ?? []).map((item) => {
        const date = DateTime.fromFormat(item.month, 'yyyy-MM-dd');
        const year = date.year;
        const month = date.month;
        return month < startBusinessYearSettingValue ? year - 1 : year;
      })
    )
  ).sort((a, b) => a - b);

  const calculatedMinYear =
    fiscalYears.length > 0 ? Math.min(...fiscalYears) : NaN;
  const calculatedMaxYear =
    fiscalYears.length > 0 ? Math.max(...fiscalYears) : NaN;

  const minDate = useMemo(() => {
    if (!isNaN(calculatedMinYear) && calculatedMinYear > 0) {
      return new Date(calculatedMinYear, 0);
    }

    if (oldestInvoice?.data?.[0]?.date) {
      return new Date(oldestInvoice.data[0].date);
    }

    return new Date();
  }, [calculatedMinYear, oldestInvoice]);

  const maxDate = useMemo(() => {
    if (!isNaN(calculatedMaxYear) && calculatedMaxYear > 0) {
      return new Date(calculatedMaxYear, 0);
    }

    return maxYear ? maxYear : new Date();
  }, [calculatedMaxYear, maxYear]);

  useEffect(() => {
    if (supplier) {
      const selectedYear = DateTime.fromJSDate(currentYear).year;
      if (!fiscalYears.includes(selectedYear)) {
        setCurrentYear(maxDate);
      }
    }
  }, [supplier, currentYear, maxDate, data, setCurrentYear, fiscalYears]);

  const handleSupplierChange = (supplier: Supplier) => {
    updateSearchParams(
      StatisticSupplierSheetContainerSearchParamKeys.id,
      supplier?.id
    );
  };

  const isLoading = data.loading || data.pending;

  return (
    <>
      {isLoading && (
        <LinearProgress
          style={{ position: 'absolute', width: '100%', zIndex: 200 }}
        />
      )}
      <div className={styles.controlBar}>
        <div style={{ width: '15rem', maxWidth: '50%' }}>
          <StatisticDateWheelPickerField
            min={minDate}
            max={maxDate}
            unit="year"
            label={t('COMMON.BUSINESS_YEAR')}
            value={currentYear}
            onChange={(value) => {
              setCurrentYear(value);
            }}
          />
        </div>
        <div style={{ width: '15rem', maxWidth: '50%' }}>
          <SupplierPickerField
            value={supplier}
            onChange={handleSupplierChange}
          />
        </div>
      </div>
      <div className={styles.tableWrapper} style={{ padding: '0 1rem' }}>
        <StatisticSupplierSheetTable
          data={data.data as StatisticSupplierDatasheet}
          selectedYear={currentYear}
          selectedCustomer={supplier}
          borders={tablesLayoutBorders.value}
          startBusinessYearSetting={startBusinessYearSettingValue}
          onDateChange={setCurrentYear}
        />
      </div>
    </>
  );
}
